import {
    PartnershipScenario,
    CalculatedResults,
    MonthlyProjection,
    QuarterlyProjection,
    AnnualProjection,
    MarketExpansionPotential
} from '../types/types';

// Constants for calculations
const DISCOUNT_RATE = 0.10; // 10% annual discount rate for NPV calculations

// Market expansion potential multipliers
const MARKET_EXPANSION_MULTIPLIERS: Record<MarketExpansionPotential, number> = {
    low: 1.1,
    medium: 1.3,
    high: 1.5
};

/**
 * COMPLETELY REWRITTEN: Calculates monthly investment based on the scenario parameters
 * DEBUG MODE: Logs every calculation step
 */
const calculateMonthlyInvestment = (scenario: PartnershipScenario, month: number): number => {
    const { investmentMetrics } = scenario;
    
    console.log(`\n=== MONTH ${month} INVESTMENT CALCULATION ===`);
    
    let total = 0;
    
    // One-time costs in month 1 only
    if (month === 1) {
        const acquisitionCosts = Number(investmentMetrics.partnerAcquisitionCosts) || 0;
        const techCosts = Number(investmentMetrics.technologySetupCosts) || 0;
        const trainingCosts = Number(investmentMetrics.trainingCosts) || 0;
        
        total += acquisitionCosts;
        total += techCosts;
        total += trainingCosts;
        
        console.log(`Month ${month} - One-time costs:`, {
            acquisition: acquisitionCosts,
            tech: techCosts,
            training: trainingCosts,
            oneTimeTotal: total
        });
    }
    
    // Annual costs divided by 12 for monthly calculation
    const monthlyManagement = (Number(investmentMetrics.partnerManagementCosts) || 0) / 12;
    const monthlyMarketing = (Number(investmentMetrics.partnerMarketingCosts) || 0) / 12;
    
    total += monthlyManagement;
    total += monthlyMarketing;
    
    console.log(`Month ${month} - Monthly costs:`, {
        management: monthlyManagement,
        marketing: monthlyMarketing,
        monthlyTotal: monthlyManagement + monthlyMarketing,
        totalInvestment: total
    });
    
    return total;
};

/**
 * COMPLETELY REWRITTEN: Calculates monthly returns based on the scenario parameters
 * DEBUG MODE: Logs every calculation step
 * FIXED: Removed excessive multipliers for realistic results
 */
const calculateMonthlyReturns = (scenario: PartnershipScenario, month: number): number => {
    const { returnMetrics } = scenario;
    
    console.log(`\n=== MONTH ${month} RETURNS CALCULATION ===`);
    
    // Basic monthly revenue (divided by 12 for monthly)
    const monthlyRevenue = (Number(returnMetrics.projectedRevenue) || 0) / 12;
    
    // Lead generation value (monthly) - ONLY if projected revenue is 0
    let monthlyLeadValue = 0;
    if (monthlyRevenue === 0) {
        const monthlyLeads = Number(returnMetrics.leadGenerationVolume) || 0;
        const conversionRate = Number(returnMetrics.leadConversionRate) || 0;
        const dealSize = Number(returnMetrics.averageDealSize) || 0;
        monthlyLeadValue = (monthlyLeads * conversionRate / 100 * dealSize) / 12;
    }
    
    // Market expansion multiplier (reduced impact)
    const marketMultiplier = MARKET_EXPANSION_MULTIPLIERS[returnMetrics.marketExpansionPotential];
    
    // Retention and sales cycle factors (reduced impact)
    const retentionFactor = 1 + (Number(returnMetrics.customerRetentionImprovement) || 0) / 200; // Reduced from /100 to /200
    const salesCycleFactor = 1 + (Number(returnMetrics.salesCycleReduction) || 0) / 200; // Reduced from /100 to /200
    
    // Calculate total returns with realistic multipliers
    const baseReturns = monthlyRevenue + monthlyLeadValue;
    const totalReturns = baseReturns * marketMultiplier * retentionFactor * salesCycleFactor;
    
    console.log(`Month ${month} - Returns breakdown:`, {
        monthlyRevenue,
        monthlyLeadValue,
        baseReturns,
        marketMultiplier,
        retentionFactor,
        salesCycleFactor,
        totalReturns,
        multiplierTotal: marketMultiplier * retentionFactor * salesCycleFactor
    });
    
    return totalReturns;
};

/**
 * COMPLETELY REWRITTEN: Calculates the Net Present Value (NPV) of cash flows
 */
const calculateNPV = (cashFlows: number[]): number => {
    const monthlyDiscountRate = DISCOUNT_RATE / 12;
    let npv = 0;
    
    console.log('\n=== NPV CALCULATION ===');
    
    cashFlows.forEach((cashFlow, index) => {
        const discountedValue = cashFlow / Math.pow(1 + monthlyDiscountRate, index + 1);
        npv += discountedValue;
        console.log(`Month ${index + 1}: CF=${cashFlow}, Discounted=${discountedValue.toFixed(2)}, NPV=${npv.toFixed(2)}`);
    });
    
    console.log(`Final NPV: ${npv.toFixed(2)}`);
    return npv;
};

/**
 * COMPLETELY REWRITTEN: Calculates risk-adjusted ROI based on risk metrics
 */
const calculateRiskAdjustedROI = (roi: number, scenario: PartnershipScenario): number => {
    const { riskMetrics } = scenario;
    
    console.log('\n=== RISK ADJUSTMENT CALCULATION ===');
    
    const riskFactor = 1 - (
        (Number(riskMetrics.partnerChurnProbability) || 0) / 100 +
        (Number(riskMetrics.resourceAllocationRisk) || 0) / 100 +
        (Number(riskMetrics.dependencyRisk) || 0) / 100
    );

    const volatilityImpact = {
        low: 0.95,
        medium: 0.85,
        high: 0.75
    }[riskMetrics.marketVolatilityImpact];

    const adjustedROI = roi * riskFactor * volatilityImpact;
    
    console.log('Risk adjustment:', {
        originalROI: roi,
        riskFactor,
        volatilityImpact,
        adjustedROI
    });
    
    return adjustedROI;
};

/**
 * COMPLETELY REWRITTEN: Calculates break-even point in months
 * DEBUG MODE: Logs every step
 * FIXED: More realistic break-even calculation
 */
const calculateBreakEvenMonths = (monthlyProjections: MonthlyProjection[]): number => {
    console.log('\n=== BREAK-EVEN CALCULATION ===');
    
    let cumulativeNetCashFlow = 0;
    let totalInvestment = 0;
    
    // First, calculate total initial investment (one-time costs)
    for (let i = 0; i < monthlyProjections.length; i++) {
        if (i === 0) {
            totalInvestment = monthlyProjections[i].investment;
        }
    }
    
    console.log(`Total initial investment to recover: ${totalInvestment.toFixed(2)}`);
    
    for (let i = 0; i < monthlyProjections.length; i++) {
        const month = monthlyProjections[i];
        cumulativeNetCashFlow += month.netCashFlow;
        
        console.log(`Month ${i + 1}: Net CF=${month.netCashFlow.toFixed(2)}, Cumulative=${cumulativeNetCashFlow.toFixed(2)}`);
        
        // Break-even when we've recovered the initial investment
        if (cumulativeNetCashFlow >= 0) {
            console.log(`BREAK-EVEN REACHED at month ${i + 1}!`);
            return i + 1;
        }
    }
    
    console.log(`NEVER BREAK-EVEN! Final cumulative: ${cumulativeNetCashFlow.toFixed(2)}`);
    return Infinity;
};

/**
 * COMPLETELY REWRITTEN: Main function to calculate all ROI metrics for a partnership scenario
 * DEBUG MODE: Logs every calculation step
 */
export const calculateROI = (scenario: PartnershipScenario): CalculatedResults => {
    console.log('\n\n==========================================');
    console.log('🚀 STARTING COMPLETE ROI CALCULATION');
    console.log('==========================================');
    
    console.log('\n📊 INPUT SCENARIO:');
    console.log('Investment Metrics:', scenario.investmentMetrics);
    console.log('Return Metrics:', scenario.returnMetrics);
    console.log('Risk Metrics:', scenario.riskMetrics);
    
    const totalMonths = scenario.timeframe * 12;
    let totalInvestment = 0;
    let totalReturns = 0;
    
    console.log(`\n⏱️  Timeframe: ${scenario.timeframe} year(s) = ${totalMonths} months`);
    
    // Calculate monthly projections
    const monthlyProjections: MonthlyProjection[] = [];
    const monthlyNetCashFlows: number[] = [];

    console.log('\n📈 MONTHLY PROJECTIONS:');
    
    for (let month = 1; month <= totalMonths; month++) {
        const investment = calculateMonthlyInvestment(scenario, month);
        const returns = calculateMonthlyReturns(scenario, month);
        const netCashFlow = returns - investment;

        totalInvestment += investment;
        totalReturns += returns;
        monthlyNetCashFlows.push(netCashFlow);

        const cumulativeROI = totalInvestment > 0 ? ((totalReturns - totalInvestment) / totalInvestment) * 100 : 0;
        
        monthlyProjections.push({
            month,
            investment,
            returns,
            netCashFlow,
            cumulativeROI
        });
        
        console.log(`Month ${month} Summary: Investment=${investment.toFixed(2)}, Returns=${returns.toFixed(2)}, Net=${netCashFlow.toFixed(2)}, Cumulative ROI=${cumulativeROI.toFixed(2)}%`);
    }

    // Calculate quarterly projections
    const quarterlyProjections: QuarterlyProjection[] = [];
    console.log('\n📊 QUARTERLY PROJECTIONS:');
    
    for (let quarter = 0; quarter < totalMonths / 3; quarter++) {
        const quarterData = monthlyProjections.slice(quarter * 3, (quarter + 1) * 3);
        const quarterInvestment = quarterData.reduce((sum, m) => sum + m.investment, 0);
        const quarterReturns = quarterData.reduce((sum, m) => sum + m.returns, 0);
        const quarterNetCashFlow = quarterData.reduce((sum, m) => sum + m.netCashFlow, 0);
        const cumulativeROI = monthlyProjections[(quarter + 1) * 3 - 1].cumulativeROI;
        
        quarterlyProjections.push({
            quarter: quarter + 1,
            investment: quarterInvestment,
            returns: quarterReturns,
            netCashFlow: quarterNetCashFlow,
            cumulativeROI
        });
        
        console.log(`Q${quarter + 1}: Investment=${quarterInvestment.toFixed(2)}, Returns=${quarterReturns.toFixed(2)}, Net=${quarterNetCashFlow.toFixed(2)}, ROI=${cumulativeROI.toFixed(2)}%`);
    }

    // Calculate annual projections
    const annualProjections: AnnualProjection[] = [];
    console.log('\n📅 ANNUAL PROJECTIONS:');
    
    for (let year = 0; year < totalMonths / 12; year++) {
        const yearData = monthlyProjections.slice(year * 12, (year + 1) * 12);
        const yearInvestment = yearData.reduce((sum, m) => sum + m.investment, 0);
        const yearReturns = yearData.reduce((sum, m) => sum + m.returns, 0);
        const yearNetCashFlow = yearData.reduce((sum, m) => sum + m.netCashFlow, 0);
        const cumulativeROI = monthlyProjections[(year + 1) * 12 - 1].cumulativeROI;
        
        annualProjections.push({
            year: year + 1,
            investment: yearInvestment,
            returns: yearReturns,
            netCashFlow: yearNetCashFlow,
            cumulativeROI
        });
        
        console.log(`Year ${year + 1}: Investment=${yearInvestment.toFixed(2)}, Returns=${yearReturns.toFixed(2)}, Net=${yearNetCashFlow.toFixed(2)}, ROI=${cumulativeROI.toFixed(2)}%`);
    }

    // Final calculations
    const baseROIPercentage = totalInvestment > 0 ? ((totalReturns - totalInvestment) / totalInvestment) * 100 : 0;
    const netPresentValue = calculateNPV(monthlyNetCashFlows);
    const riskAdjustedROIPercentage = calculateRiskAdjustedROI(baseROIPercentage, scenario);
    const breakEvenMonths = calculateBreakEvenMonths(monthlyProjections);

    console.log('\n🎯 FINAL RESULTS:');
    console.log('Total Investment:', totalInvestment.toFixed(2));
    console.log('Total Returns:', totalReturns.toFixed(2));
    console.log('Base ROI %:', baseROIPercentage.toFixed(2));
    console.log('Risk-Adjusted ROI %:', riskAdjustedROIPercentage.toFixed(2));
    console.log('Break-even Months:', breakEvenMonths === Infinity ? 'Never' : breakEvenMonths);
    console.log('Net Present Value:', netPresentValue.toFixed(2));
    
    console.log('\n==========================================');
    console.log('✅ ROI CALCULATION COMPLETE');
    console.log('==========================================\n');

    return {
        totalInvestment,
        totalReturns,
        baseROIPercentage,
        riskAdjustedROIPercentage,
        breakEvenMonths,
        monthlyProjections,
        quarterlyProjections,
        annualProjections,
        netPresentValue
    };
}; 